import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_API_URL } from '../helpers/Config';
import toast from 'react-hot-toast';
import { TriggerBalanceUpdate } from '../helpers/TriggerBalanceUpdate';

const UsdtSwap = ({ theme = 'dark' }) => {
    const [activeTab, setActiveTab] = useState('usdt to coin');
    const [inputAmount, setInputAmount] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [coinRate, setCoinRate] = useState(0);
    const [userBalance, setUserBalance] = useState({
        totalCoins: 0,
        lockedCoins: 0,
        swapableCoins: 0,
        usdtBalance: 0
    });
    const [swapReport, setSwapReport] = useState([]);
    const [isLoadingSwapReport, setIsLoadingSwapReport] = useState(false);

    const navigate = useNavigate();

    // THEME MAP
    const isDark = theme === 'dark';
    const themeColors = {
        bodyBg: isDark ? '#020617' : '#f8f9fa',
        surfaceBg: isDark ? 'rgba(15,23,42,0.9)' : 'var(--bs-tertiary-bg)',
        arrowColor: isDark ? '#9ca3af' : '#6c757d',
        receiveBg: isDark ? 'rgba(34,197,94,0.16)' : 'rgba(25,135,84,0.08)',
        receiveColor: isDark ? '#4ade80' : '#198754',
    };

    // Reset input amount when tab changes
    useEffect(() => {
        setInputAmount('');
    }, [activeTab]);

    // Calculate output amount based on input and direction
    const calculateOutput = () => {
        if (!inputAmount || isNaN(parseFloat(inputAmount)) || coinRate === 0) return '0.00';

        const amount = parseFloat(inputAmount);
        if (activeTab === 'usdt to coin') {
            // USDT to Coin: 1 USDT = coinRate coins
            return (amount * coinRate).toFixed(2);
        } else {
            // Coin to USDT: 1 coin = (1/coinRate) USDT
            return (amount / coinRate).toFixed(2);
        }
    };

    const FetchProfile = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/account/profile`, {
                token: localStorage.getItem('a5srf4wer7w1cd')
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.data) {
                setUserBalance({
                    totalCoins: parseFloat(data.data.balance) || 0,
                    usdtBalance: parseFloat(data.data.usdt) || 0,
                    lockedCoins: parseFloat(data.data.lock_balance) || 0,
                    swapableCoins:
                        (parseFloat(data.data.balance) - parseFloat(data.data.lock_balance)) || 0
                });
                const balance = data?.data?.balance;
                const usdt = data?.data?.usdt;
                TriggerBalanceUpdate({ balance, usdt });
            }
        } catch (error) {
            console.log('Error fetching profile:', error);
            toast.error('Failed to load balance information');
        }
    };

    const FetchCoinRate = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/payment/coin-rate`, {
                token: localStorage.getItem('a5srf4wer7w1cd')
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.coin) {
                setCoinRate(parseFloat(data.coin));
            }
        } catch (error) {
            console.log('Error fetching coin rate:', error);
            toast.error('Failed to load coin rate');
        }
    };

    const FetchSwapReport = async () => {
        setIsLoadingSwapReport(true);
        try {
            const response = await axios.post(`${BASE_API_URL}/payment/swap-report`, {
                token: localStorage.getItem('a5srf4wer7w1cd')
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    console.error(data?.error);
                }
            }

            if (data?.data) {
                // Take only first 5 items
                setSwapReport(data.data.slice(0, 5));
            }
        } catch (error) {
            console.log('Error fetching swap report:', error);
        } finally {
            setIsLoadingSwapReport(false);
        }
    };

    useEffect(() => {
        FetchProfile();
        FetchCoinRate();
        FetchSwapReport();
    }, []);

    const handleMaxClick = () => {
        if (activeTab === 'usdt to coin') {
            setInputAmount(userBalance.usdtBalance.toString());
        } else {
            setInputAmount(userBalance.swapableCoins.toString());
        }
    };

    const handleSwap = () => {
        if (!inputAmount || parseFloat(inputAmount) <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        const amount = parseFloat(inputAmount);
        if (activeTab === 'usdt to coin' && amount > userBalance.usdtBalance) {
            toast.error('Insufficient USDT balance');
            return;
        }

        if (activeTab === 'coin to usdt' && amount > userBalance.swapableCoins) {
            toast.error('Insufficient swapable coins');
            return;
        }

        // Updated minimum amounts
        if (activeTab === 'usdt to coin' && amount < 1) {
            toast.error('Minimum swap amount is 1 USDT');
            return;
        }

        if (activeTab === 'coin to usdt' && amount < 100) {
            toast.error('Minimum swap amount is 100 coins');
            return;
        }

        setShowConfirmModal(true);
    };

    const handleConfirmSwap = async () => {
        setIsProcessing(true);
        try {
            const response = await axios.post(`${BASE_API_URL}/payment/swap`, {
                token: localStorage.getItem('a5srf4wer7w1cd'),
                type: activeTab,
                amount: inputAmount
            });

            const data = response?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error);
                }
            } else if (data?.msg) {
                toast.success(data?.msg);
                setInputAmount('');
                FetchProfile();
                FetchSwapReport(); // Refresh swap report after successful swap
            }
        } catch (error) {
            console.log('Swap error:', error);
            toast.error('Failed to process swap');
        } finally {
            setIsProcessing(false);
            setShowConfirmModal(false);
        }
    };

    const formatNumber = (num) => {
        const n = parseFloat(num);
        if (isNaN(n)) return '0.00';
        return n.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    // Calculate USDT per coin for display
    const getUSDTPerCoin = () => {
        if (coinRate === 0) return '0';
        return (1 / coinRate).toFixed(4);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Navigate to swap report page
    const handleViewAllSwapReport = () => {
        navigate('/swap-report');
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="container-fluid px-3 py-3"
            style={{
                maxWidth: '480px',
                margin: '0 auto',
                minHeight: '100vh',
                backgroundColor: themeColors.bodyBg
            }}
        >
            {/* Header Section */}
            <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="text-center mb-4"
            >
                <div className="d-flex align-items-center justify-content-center mb-2">
                    <span className="me-2" style={{ fontSize: '1.8rem' }}>🔄</span>
                    <h4 className="mb-0 fw-bold">Currency Swap</h4>
                </div>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                    Convert between USDT and Coins instantly
                </p>
                <small className="text-primary fw-semibold" style={{ fontSize: '0.8rem' }}>
                    Rate: 1 USDT = {coinRate} Coins
                </small>
            </motion.div>

            {/* Balance Cards */}
            <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="row g-2 mb-3"
            >
                {/* Total Coins */}
                <div className="col-4">
                    <div className="card border-0 text-white" style={styles.totalCoinCard}>
                        <div className="card-body p-2 text-center">
                            <h6 className="card-title mb-1 opacity-75" style={{ fontSize: '0.7rem' }}>
                                Total Coins
                            </h6>
                            <h5 className="mb-0 fw-bold" style={{ fontSize: '0.8rem' }}>
                                {formatNumber(userBalance.totalCoins)}
                            </h5>
                        </div>
                    </div>
                </div>

                {/* Locked Coins */}
                <div className="col-4">
                    <div className="card border-0 text-white" style={styles.lockedCoinCard}>
                        <div className="card-body p-2 text-center">
                            <h6 className="card-title mb-1 opacity-75" style={{ fontSize: '0.7rem' }}>
                                Locked Coins
                            </h6>
                            <h5 className="mb-0 fw-bold" style={{ fontSize: '0.8rem' }}>
                                {formatNumber(userBalance.lockedCoins)}
                            </h5>
                        </div>
                    </div>
                </div>

                {/* Swapable Coins */}
                <div className="col-4">
                    <div className="card border-0 text-white" style={styles.swapableCoinCard}>
                        <div className="card-body p-2 text-center">
                            <h6 className="card-title mb-1 opacity-75" style={{ fontSize: '0.7rem' }}>
                                Swapable Coins
                            </h6>
                            <h5 className="mb-0 fw-bold" style={{ fontSize: '0.8rem' }}>
                                {formatNumber(userBalance.swapableCoins)}
                            </h5>
                        </div>
                    </div>
                </div>

                {/* USDT Balance */}
                <div className="col-12 mt-2">
                    <div className="card border-0 text-white" style={styles.usdtCard}>
                        <div className="card-body p-2 text-center">
                            <h6 className="card-title mb-1 opacity-75" style={{ fontSize: '0.75rem' }}>
                                USDT Balance
                            </h6>
                            <h5 className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>
                                ${formatNumber(userBalance.usdtBalance)}
                            </h5>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Swap Interface */}
            <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="card border-0 shadow-sm mb-3"
            >
                <div className="card-body p-0">
                    {/* Tab Selection */}
                    <div className="btn-group w-100" role="group">
                        <button
                            type="button"
                            className={`btn ${activeTab === 'usdt to coin' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setActiveTab('usdt to coin')}
                            style={{ fontSize: '0.85rem', padding: '10px' }}
                        >
                            USDT → Coins
                        </button>
                        <button
                            type="button"
                            className={`btn ${activeTab === 'coin to usdt' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setActiveTab('coin to usdt')}
                            style={{ fontSize: '0.85rem', padding: '10px' }}
                        >
                            Coins → USDT
                        </button>
                    </div>

                    <div className="p-3">
                        {/* Input Section */}
                        <div className="mb-3">
                            <label className="form-label text-muted mb-2 fw-semibold" style={{ fontSize: '0.8rem' }}>
                                {activeTab === 'usdt to coin' ? 'YOU PAY (USDT)' : 'YOU PAY (Coins)'}
                            </label>
                            <div className="input-group">
                                <span
                                    className="input-group-text border-end-0 fw-bold"
                                    style={{ backgroundColor: themeColors.surfaceBg }}
                                >
                                    {activeTab === 'usdt to coin' ? '$' : '🪙'}
                                </span>
                                <input
                                    type="number"
                                    className="form-control border-start-0 fw-bold"
                                    placeholder="0.00"
                                    value={inputAmount}
                                    onChange={(e) => setInputAmount(e.target.value)}
                                    style={{ fontSize: '1.1rem' }}
                                />
                                <button
                                    className="btn btn-outline-primary"
                                    type="button"
                                    onClick={handleMaxClick}
                                    style={{ fontSize: '0.75rem' }}
                                >
                                    MAX
                                </button>
                            </div>
                            <div className="d-flex justify-content-between mt-1">
                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                    Available:{' '}
                                    {activeTab === 'usdt to coin'
                                        ? `$${formatNumber(userBalance.usdtBalance)}`
                                        : `${formatNumber(userBalance.swapableCoins)} coins`}
                                </small>
                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                    Min: {activeTab === 'usdt to coin' ? '1 USDT' : '100 coins'}
                                </small>
                            </div>
                        </div>

                        {/* Swap Arrow */}
                        <div className="text-center my-2">
                            <motion.div
                                animate={{ rotate: 180 }}
                                transition={{ duration: 0.3 }}
                                style={{ fontSize: '1.5rem', color: themeColors.arrowColor }}
                            >
                                ⬇️
                            </motion.div>
                        </div>

                        {/* Output Section */}
                        <div className="mb-3">
                            <label className="form-label text-muted mb-2 fw-semibold" style={{ fontSize: '0.8rem' }}>
                                {activeTab === 'usdt to coin' ? 'YOU RECEIVE (Coins)' : 'YOU RECEIVE (USDT)'}
                            </label>
                            <div className="input-group">
                                <span
                                    className="input-group-text border-end-0 fw-bold"
                                    style={{ backgroundColor: themeColors.surfaceBg }}
                                >
                                    {activeTab === 'usdt to coin' ? '🪙' : '$'}
                                </span>
                                <input
                                    type="text"
                                    className="form-control border-start-0 fw-bold"
                                    value={calculateOutput()}
                                    readOnly
                                    style={{
                                        fontSize: '1.1rem',
                                        backgroundColor: themeColors.receiveBg,
                                        color: themeColors.receiveColor
                                    }}
                                />
                            </div>
                        </div>

                        {/* Dynamic Rate Info */}
                        <div
                            className="p-2 rounded text-center"
                            style={{ backgroundColor: themeColors.surfaceBg }}
                        >
                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                <strong>Current Rate:</strong> 1 USDT = {coinRate} Coins
                                <br />
                                <small>(1 Coin = ${getUSDTPerCoin()} USDT)</small>
                            </small>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Preview Section (Without Charges) */}
            {inputAmount && parseFloat(inputAmount) > 0 && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="card border-0 shadow-sm mb-3"
                >
                    <div className="card-body p-3">
                        <h6 className="fw-bold mb-2" style={{ fontSize: '0.9rem' }}>Swap Preview</h6>
                        <div className="row text-muted mb-1" style={{ fontSize: '0.8rem' }}>
                            <div className="col-6">You Pay:</div>
                            <div className="col-6 text-end fw-semibold">
                                {activeTab === 'usdt to coin'
                                    ? `$${formatNumber(inputAmount)} USDT`
                                    : `${formatNumber(inputAmount)} Coins`}
                            </div>
                        </div>
                        <div className="row text-muted mb-1" style={{ fontSize: '0.8rem' }}>
                            <div className="col-6">You Receive:</div>
                            <div className="col-6 text-end fw-semibold text-success">
                                {activeTab === 'usdt to coin'
                                    ? `${formatNumber(calculateOutput())} Coins`
                                    : `$${formatNumber(calculateOutput())} USDT`}
                            </div>
                        </div>
                        <hr className="my-2" />
                        <div className="row fw-bold" style={{ fontSize: '0.9rem' }}>
                            <div className="col-6">Total Amount:</div>
                            <div className="col-6 text-end text-success">
                                {activeTab === 'usdt to coin'
                                    ? `${formatNumber(calculateOutput())} Coins`
                                    : `$${formatNumber(calculateOutput())} USDT`}
                            </div>
                        </div>
                        <small className="text-muted d-block mt-1" style={{ fontSize: '0.7rem' }}>
                            No swap charges applied
                        </small>
                    </div>
                </motion.div>
            )}

            {/* Swap Button */}
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
            >
                <motion.button
                    className="btn btn-primary w-100 py-3 fw-bold"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSwap}
                    disabled={!inputAmount || parseFloat(inputAmount) <= 0 || isProcessing || coinRate === 0}
                    style={{ fontSize: '1rem' }}
                >
                    {isProcessing ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Processing Swap...
                        </>
                    ) : coinRate === 0 ? (
                        'Loading Rates...'
                    ) : (
                        'Confirm Swap'
                    )}
                </motion.button>
            </motion.div>

            {/* Swap Report History */}
            <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="card border-0 shadow-sm mt-3"
            >
                <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold mb-0" style={{ fontSize: '0.9rem' }}>
                            Recent Swap Report
                        </h6>
                        <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={handleViewAllSwapReport}
                            style={{ fontSize: '0.75rem' }}
                        >
                            View All
                        </button>
                    </div>

                    {isLoadingSwapReport ? (
                        <div className="text-center py-3">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <small className="text-muted d-block mt-2" style={{ fontSize: '0.75rem' }}>
                                Loading swap history...
                            </small>
                        </div>
                    ) : swapReport.length > 0 ? (
                        <div className="list-group list-group-flush">
                            {swapReport.map((swap, index) => (
                                <div key={index} className="list-group-item px-0 py-2 border-0">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            <div
                                                className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${swap.type === 'usdt to coin'
                                                    ? 'bg-success'
                                                    : 'bg-primary'
                                                    }`}
                                                style={{ width: '32px', height: '32px' }}
                                            >
                                                <span style={{ fontSize: '0.8rem', color: 'white' }}>
                                                    {swap.type === 'usdt to coin' ? '🪙' : '$'}
                                                </span>
                                            </div>
                                            <div>
                                                <div
                                                    className="fw-semibold"
                                                    style={{ fontSize: '0.8rem' }}
                                                >
                                                    {swap.type === 'usdt to coin'
                                                        ? `USDT → Coins`
                                                        : `Coins → USDT`}
                                                </div>
                                                <small
                                                    className="text-muted"
                                                    style={{ fontSize: '0.7rem' }}
                                                >
                                                    {formatDate(swap.create_date)}
                                                </small>
                                            </div>
                                        </div>
                                        <div className="text-end">
                                            <div
                                                className="fw-bold text-success"
                                                style={{ fontSize: '0.8rem' }}
                                            >
                                                {swap.type === 'usdt to coin'
                                                    ? `+${formatNumber(swap.coin)} Coins`
                                                    : `+$${formatNumber(swap.usdt)} USDT`}
                                            </div>
                                            <small
                                                className="text-muted"
                                                style={{ fontSize: '0.7rem' }}
                                            >
                                                {swap.type === 'usdt to coin'
                                                    ? `$${formatNumber(swap.usdt)}`
                                                    : `${formatNumber(swap.coin)} Coins`}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-3">
                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                No swap history found
                            </span>
                        </div>
                    )}

                    {swapReport.length > 0 && (
                        <div className="text-center mt-2">
                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                Showing 5 of recent swaps
                            </small>
                        </div>
                    )}
                </div>
            </motion.div>

            {/* Information Card */}
            <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="card border-0 shadow-sm mt-3"
            >
                <div className="card-body p-3">
                    <h6 className="fw-bold mb-2" style={{ fontSize: '0.9rem' }}>Swap Information</h6>
                    <ul className="text-muted mb-0" style={{ fontSize: '0.75rem', lineHeight: '1.6' }}>
                        <li>Current exchange rate: 1 USDT = {coinRate} Coins</li>
                        <li>No swap charges - 0% fees</li>
                        <li>Minimum swap: 1 USDT or 100 Coins</li>
                        <li>Swaps are processed instantly</li>
                        <li>Locked coins cannot be swapped</li>
                        <li>No network fees for internal swaps</li>
                        <li>Rates update automatically</li>
                    </ul>
                </div>
            </motion.div>

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="modal show d-block"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                >
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="modal-dialog modal-dialog-centered"
                        style={{ maxWidth: '400px' }}
                    >
                        <div className="modal-content">
                            <div className="modal-header py-2">
                                <h6 className="modal-title fw-bold" style={{ fontSize: '0.9rem' }}>
                                    Confirm Swap
                                </h6>
                                <button
                                    type="button"
                                    className="btn-close btn-close-sm"
                                    onClick={() => !isProcessing && setShowConfirmModal(false)}
                                    disabled={isProcessing}
                                ></button>
                            </div>
                            <div className="modal-body py-3">
                                {isProcessing ? (
                                    <div className="text-center">
                                        <div
                                            className="spinner-border text-primary mb-3"
                                            role="status"
                                        >
                                            <span className="visually-hidden">Processing...</span>
                                        </div>
                                        <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                                            Processing your swap...
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-center mb-3">
                                            <div
                                                className="rounded p-3 mb-2"
                                                style={{ backgroundColor: themeColors.surfaceBg }}
                                            >
                                                <div className="fs-5 fw-bold text-primary">
                                                    {activeTab === 'usdt to coin'
                                                        ? `$${formatNumber(inputAmount)} USDT`
                                                        : `${formatNumber(inputAmount)} Coins`}
                                                </div>
                                                <small className="text-muted">You Pay</small>
                                            </div>
                                            <div className="my-2" style={{ fontSize: '1.2rem' }}>⭣</div>
                                            <div
                                                className="rounded p-3"
                                                style={{ backgroundColor: themeColors.surfaceBg }}
                                            >
                                                <div className="fs-5 fw-bold text-success">
                                                    {activeTab === 'usdt to coin'
                                                        ? `${formatNumber(calculateOutput())} Coins`
                                                        : `$${formatNumber(calculateOutput())} USDT`}
                                                </div>
                                                <small className="text-muted">You Receive</small>
                                            </div>
                                        </div>

                                        <div className="border rounded p-2 mb-3">
                                            <div className="row text-muted mb-1" style={{ fontSize: '0.8rem' }}>
                                                <div className="col-6">Exchange Rate:</div>
                                                <div className="col-6 text-end">
                                                    1 USDT = {coinRate} Coins
                                                </div>
                                            </div>
                                            <div className="row text-success mb-1" style={{ fontSize: '0.8rem' }}>
                                                <div className="col-6">Swap Charges:</div>
                                                <div className="col-6 text-end fw-semibold">
                                                    0% (No Charges)
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            className="alert alert-warning py-2"
                                            style={{ fontSize: '0.75rem' }}
                                        >
                                            <small>
                                                <strong>Please confirm:</strong> This action cannot be
                                                undone. Make sure the amounts are correct before
                                                proceeding.
                                            </small>
                                        </div>
                                    </>
                                )}
                            </div>
                            {!isProcessing && (
                                <div className="modal-footer py-2">
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => setShowConfirmModal(false)}
                                        style={{ fontSize: '0.8rem' }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        onClick={handleConfirmSwap}
                                        style={{ fontSize: '0.8rem' }}
                                    >
                                        Confirm Swap
                                    </button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}

            <style>{`
                .container-fluid {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                }
                
                .card {
                    border-radius: 12px;
                }
                
                .btn {
                    border-radius: 8px;
                    font-weight: 600;
                }
                
                .form-control {
                    border-radius: 8px;
                }
                
                .input-group-text {
                    border-radius: 8px;
                }
                
                .modal-content {
                    border-radius: 12px;
                    border: none;
                }
                
                .list-group-item:first-child {
                    border-top: 0;
                }
            `}</style>
        </motion.div>
    );
};

const styles = {
    totalCoinCard: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '10px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
    },
    lockedCoinCard: {
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        borderRadius: '10px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
    },
    swapableCoinCard: {
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        borderRadius: '10px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
    },
    usdtCard: {
        background: 'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)',
        borderRadius: '10px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
    }
};

export default UsdtSwap;